import { computed, effect, untracked } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { withAlert } from '@seatix/shared-util-alert';
import { UserUpdateDto } from '@seatix/shared/domain';
import { AuthState, initialAuthState } from '../auth-api/auth.models';
import { withAuthentication } from '../features/with-authentication';
import { withEmailVerification } from '../features/with-email-verification';
import { withPasswordReset } from '../features/with-password-reset';
import { withProfileUpdate } from '../features/with-profile-update';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(initialAuthState),
  withAuthentication(),
  withMethods((store) => ({
    setIsNewUser: (isNewUser: boolean) => {
      patchState(store, { isNewUser });
    },
  })),
  withProfileUpdate(),
  withEmailVerification(),
  withPasswordReset(),
  withComputed((store) => {
    return {
      isAuthenticated: computed(() => !!store.user()),
      hasRoles: computed(() => {
        const roles = store.user()?.roles;
        return !!roles && roles.length > 0;
      }),
      currentTeam: computed(() => store.current_team()),
      isProfileComplete: computed(() => {
        const user = store.user();
        return user ? !!user.phone : false;
      }),
      shouldShowProfileCompletion: computed(() => {
        const user = store.user();
        const isNewUser = store.isNewUser();
        const needsProfileCompletion = store.needsProfileCompletion();

        if (!user) return false;

        return (isNewUser === true || !user.phone) && needsProfileCompletion;
      }),
      isLoading: computed(
        () =>
          store.userResource.isLoading() ||
          store.isLoginLoading() ||
          store.isRegisterLoading() ||
          store.isSocialLoginLoading() ||
          store.isEmailVerificationLoading() ||
          store.isResendVerificationLoading() ||
          store.passwordResetRequestLoading() ||
          store.passwordResetLoading(),
      ),
      error: computed(
        () =>
          store.loginError() ??
          store.registerError() ??
          store.socialLoginError() ??
          store.emailVerificationError() ??
          store.resendVerificationError() ??
          store.profileUpdateError() ??
          store.passwordResetRequestError() ??
          store.passwordResetError() ??
          store.userResource.error()?.message,
      ),
    };
  }),
  withMethods((store) => {
    const updateUser = (user: UserUpdateDto) => {
      const currentUser = store.user();
      if (currentUser) {
        patchState(store, { user: { ...currentUser, ...user } });
      }
    };

    return {
      updateUser,
    };
  }),
  withAlert(),
  withHooks((store) => {
    return {
      onInit: () => {
        effect(() => {
          try {
            const isEmailVerificationLoaded = store.isEmailVerificationLoaded();

            if (isEmailVerificationLoaded) {
              const currentUser = untracked(() => store.user());

              if (currentUser) {
                patchState(store, {
                  user: {
                    ...currentUser,
                    needs_email_verification: false,
                  },
                });
              }
            }
          } catch (error) {
            console.error('Error in email verification effect:', error);
          }
        });

        effect(() => {
          try {
            const profileUpdateLoaded = store.isProfileUpdateLoaded();
            const profileUpdatedData = store.profileUpdatedData();

            if (profileUpdateLoaded && profileUpdatedData) {
              const currentUser = untracked(() => store.user());

              if (currentUser) {
                store.updateUser(profileUpdatedData);
                patchState(store, {
                  profileUpdatedData: null,
                  needsProfileCompletion: false,
                  profileUpdateCallStatus: 'loaded',
                });
              }
            }
          } catch (error) {
            console.error('Error in profile update success effect:', error);
          }
        });

        effect(() => {
          try {
            const currentUser = store.user();
            const currentTeam = store.current_team();

            if (
              currentUser?.teams &&
              currentUser.teams.length > 0 &&
              !currentTeam
            ) {
              store.setCurrentTeam(currentUser.teams[0]);
            }
          } catch (error) {
            console.error('Error in team selection effect:', error);
          }
        });

        effect(() => {
          try {
            const isSocialLoginLoaded = store.isSocialLoginLoaded();

            if (isSocialLoginLoaded) {
              const currentUser = untracked(() => store.user());

              if (currentUser) {
                const needsCompletion = !currentUser.phone;

                patchState(store, {
                  needsProfileCompletion: needsCompletion,
                });
              }
            }
          } catch (error) {
            console.error(
              'Error in social login profile completion effect:',
              error,
            );
          }
        });
      },
    };
  }),
);
